import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ─── Typing indicator ───────────────────────────────────────────── */
function TypingDots() {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5, padding:'10px 14px',
      background:'rgba(255,255,255,.07)', borderRadius:'16px 16px 16px 4px',
      width:'fit-content', marginBottom:8 }}>
      {[0,1,2].map(i => (
        <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:'#64748b',
          animation:`typingBounce 1.1s ${i*.18}s ease-in-out infinite` }} />
      ))}
    </div>
  );
}

/* ─── Chat bubble with slide animation ───────────────────────────── */
function MsgBubble({ side, text }) {
  return (
    <div style={{ display:'flex', justifyContent: side==='guest' ? 'flex-end' : 'flex-start',
      marginBottom:8,
      animation: side==='guest'
        ? 'chatSlideR .38s cubic-bezier(.22,1,.36,1) both'
        : 'chatSlideL .38s cubic-bezier(.22,1,.36,1) both' }}>
      <div style={{ background: side==='guest' ? '#4f46e5' : 'rgba(255,255,255,.07)',
        color:'#e2e8f0', padding:'9px 14px', fontSize:13, lineHeight:1.5,
        maxWidth:'82%',
        borderRadius: side==='guest' ? '16px 16px 4px 16px' : '16px 16px 16px 4px' }}>
        {text}
      </div>
    </div>
  );
}

/* ─── Chat widget — typing simulation, replays on scroll ─────────── */
const CHAT = [
  { side:'guest', text:'Hi! 👋 Table for 4 this Friday evening?' },
  { side:'ai',    text:'Hi there! 😊 We have availability at 7:00 PM and 8:30 PM — which works for you?' },
  { side:'guest', text:'7pm is perfect 🙌' },
  { side:'ai',    text:"Perfect! 🎉 Can I get your name and a contact number to confirm?" },
];

function ChatWidget() {
  const [phase, setPhase] = useState(0);
  const ref       = useRef(null);
  const timers    = useRef([]);
  const hasPlayed = useRef(false);

  const run = useCallback(() => {
    if (hasPlayed.current) return;
    hasPlayed.current = true;
    const t = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    t(() => setPhase(1), 300);
    t(() => setPhase(2), 850);
    t(() => setPhase(3), 2000);
    t(() => setPhase(4), 2700);
    t(() => setPhase(5), 3200);
    t(() => setPhase(6), 4300);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) run(); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); timers.current.forEach(clearTimeout); };
  }, [run]);

  // All 4 message slots are always in the DOM (no layout shift).
  // Visibility is driven by opacity + translateY transition only.
  const show = (n) => ({
    opacity: phase >= n ? 1 : 0,
    transform: phase >= n ? 'none' : 'translateY(8px)',
    transition: 'opacity .4s ease, transform .4s ease',
    pointerEvents: phase >= n ? 'auto' : 'none',
  });

  return (
    <div ref={ref} style={{ marginTop:72, maxWidth:440, width:'100%',
      animation:'heroFadeUp 1.1s .3s cubic-bezier(.22,1,.36,1) both' }}>
      <div style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)',
        borderRadius:22, padding:'22px 24px', backdropFilter:'blur(20px)',
        WebkitBackdropFilter:'blur(20px)', boxShadow:'0 40px 100px rgba(0,0,0,.55)',
        animation:'floatBob 4.5s ease-in-out infinite' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:'#10b981',
            boxShadow:'0 0 0 3px rgba(16,185,129,.2)' }} />
          <span style={{ fontSize:12, color:'#475569', fontWeight:600 }}>Seva AI · Online now</span>
        </div>

        {/* All messages always in DOM — only opacity/transform changes, no layout shift */}
        <div style={show(1)}><MsgBubble side="guest" text={CHAT[0].text} /></div>

        {/* Typing dots: fixed height slot so they don't shift layout */}
        <div style={{ height:34, marginBottom:8, display:'flex', alignItems:'center' }}>
          <div style={{ opacity: phase === 2 ? 1 : 0, transition:'opacity .25s ease' }}>
            <TypingDots />
          </div>
        </div>

        <div style={show(3)}><MsgBubble side="ai"    text={CHAT[1].text} /></div>
        <div style={show(4)}><MsgBubble side="guest" text={CHAT[2].text} /></div>

        <div style={{ height:34, marginBottom:8, display:'flex', alignItems:'center' }}>
          <div style={{ opacity: phase === 5 ? 1 : 0, transition:'opacity .25s ease' }}>
            <TypingDots />
          </div>
        </div>

        <div style={show(6)}><MsgBubble side="ai"    text={CHAT[3].text} /></div>
      </div>
    </div>
  );
}

/* ─── SVG icon box ───────────────────────────────────────────────── */
function Icon({ children, color = '#818cf8' }) {
  return (
    <div style={{ width:44, height:44, borderRadius:12,
      background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.2)',
      display:'flex', alignItems:'center', justifyContent:'center',
      marginBottom:18, flexShrink:0 }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </svg>
    </div>
  );
}

const FEATURES = [
  { title:'24/7 AI Booking Agent',    desc:"Your AI concierge never sleeps. It handles reservations in any language, at any hour, with the warmth of a seasoned host.",                icon:<><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/></> },
  { title:'Zero Double-Bookings',      desc:'Real-time table management ensures the AI checks live availability before confirming anything — ever.',                                   icon:<><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4"/></> },
  { title:'Visual Floor Plan',         desc:"Drag-and-drop floor plan so you always know exactly what's happening on the floor at a glance.",                                         icon:<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
  { title:'Analytics & Insights',      desc:'Understand your busiest times, top tables, and booking trends. Make smarter decisions with real data.',                                   icon:<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></> },
  { title:'Multi-language',            desc:"The AI replies in the customer's own language — English, French, Spanish, Arabic, Japanese, and 15+ more.",                              icon:<><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></> },
  { title:'Automated Confirmations',   desc:'Guests get instant email confirmations with everything they need. No manual follow-up required.',                                          icon:<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></> },
  { title:'Waitlist Management',       desc:"When you're fully booked, Seva suggests the next available time first. If that doesn't work, it offers a waitlist spot and notifies the guest automatically when a table frees up.",                                              icon:<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></> },
  { title:'Capacity Planning',         desc:'A visual calendar shows availability across weeks so you can optimise seating and staffing.',                                             icon:<><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></> },
];

const COMPARE = [
  { label:'Booking fees',      seva:'None — flat monthly rate',           them:'Per-cover fees or % of bill' },
  { label:'Availability',      seva:'24/7 AI, never misses a booking',    them:'Passive widget only' },
  { label:'Large parties',     seva:'Auto-combines tables, confirms live', them:'"Call us for 10+ guests"' },
  { label:'Languages',         seva:'20+ detected automatically',         them:'One language, hard-coded' },
  { label:'Special requests',  seva:'AI logs & assigns the right table',  them:'No field — staff handle it manually' },
  { label:'Fully booked',      seva:'Offers waitlist, notifies on cancel', them:'Shows nothing, guest leaves' },
  { label:'Customer data',     seva:'Owned by you, always',               them:'Shared with the platform' },
  { label:'Customisation',     seva:'Full control of AI & brand voice',   them:'Locked to their templates' },
];

const SCENARIOS = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    color: '#f59e0b', colorBg: 'rgba(245,158,11,.10)', colorBorder: 'rgba(245,158,11,.25)',
    scenario: 'Party of 12 wants to book',
    them: { label: 'Basic widget', text: 'Shows "no availability". A note says call us for groups over 10. Guest calls during service — or doesn\'t bother.' },
    seva: { text: 'AI instantly checks which tables can be combined, books T3 + T4 + T5 as one 14-seat arrangement, and sends the confirmation. No phone call needed.' },
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    color: '#6366f1', colorBg: 'rgba(99,102,241,.10)', colorBorder: 'rgba(99,102,241,.25)',
    scenario: '"Can we sit by the window? It\'s our anniversary 🥂"',
    them: { label: 'Basic widget', text: 'No field for this. Guest picks a time slot and hopes for the best. Staff find out mid-service and scramble.' },
    seva: { text: 'AI picks it up in conversation, logs the request, picks the window table from your floor plan, and leaves a note for your team before they arrive.' },
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4"/>
      </svg>
    ),
    color: '#ef4444', colorBg: 'rgba(239,68,68,.09)', colorBorder: 'rgba(239,68,68,.22)',
    scenario: 'Guest tries to book on a sold-out night',
    them: { label: 'Basic widget', text: 'Shows no time slots. Guest closes the tab and books your competitor down the street.' },
    seva: { text: 'Seva suggests the next available date and time first. If the guest can\'t make it, they\'re added to the waitlist and notified automatically the moment a table frees up.' },
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a5 5 0 1 0 0 10A5 5 0 0 0 12 2z"/><path d="M2 20c0-4 4-7 10-7s10 3 10 7"/>
        <line x1="18" y1="8" x2="23" y2="8"/><line x1="21" y1="5" x2="21" y2="11"/>
      </svg>
    ),
    color: '#10b981', colorBg: 'rgba(16,185,129,.10)', colorBorder: 'rgba(16,185,129,.25)',
    scenario: 'Guest books at 2 AM in French',
    them: { label: 'Basic widget', text: 'Sends a form in whatever language the site is in. No conversation. No warmth. No follow-up if something looks off.' },
    seva: { text: 'AI greets the guest in French, handles the full booking, sends a confirmation email, and flags any edge cases for your staff to review in the morning.' },
  },
];

const STEPS = [
  { n:'01', title:'Customer starts a chat',  desc:'A visitor hits your website, clicks the Seva widget, and starts typing — in their own language. The AI greets them like a seasoned host.' },
  { n:'02', title:'AI handles everything',   desc:'Seva collects party size, date, time and any special requests — checking live availability at every step. No double-bookings, ever.' },
  { n:'03', title:'Confirmed in seconds',    desc:"A confirmation goes to the guest's inbox. The booking appears in your staff dashboard in real time — and you didn't lift a finger." },
];

/* ─── Wave divider ───────────────────────────────────────────────── */
function Wave({ from, to, flip }) {
  return (
    <div style={{ display:'block', lineHeight:0, background:to }}>
      <svg viewBox="0 0 1440 90" preserveAspectRatio="none"
        style={{ display:'block', width:'100%', height:90, background:from, transform: flip ? 'scaleX(-1)' : undefined }}>
        <path d="M0,45 C240,90 480,0 720,45 C960,90 1200,0 1440,45 L1440,90 L0,90 Z" fill={to} />
      </svg>
    </div>
  );
}

/* ─── CountUp ────────────────────────────────────────────────────── */
function CountUp({ to, prefix='', suffix='', duration=1600, triggered }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!triggered) return;
    if (to === 0) { setVal(0); return; }
    let raf;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [triggered, to, duration]);
  return <>{prefix}{val}{suffix}</>;
}

const STATS_DATA = [
  { prefix:'', to:24, suffix:' / 7', label:'Always available' },
  { prefix:'', to:20, suffix:'+',    label:'Languages'        },
  { prefix:'< ', to:5, suffix:' min', label:'Avg. booking time' },
  { prefix:'€', to:0, suffix:'',     label:'Per-cover fees'   },
];

/* ─── Main component starts below ───────────────────────────────── */
function SChatBubble({ side, text }) {
  return (
    <div style={{ display:'flex', justifyContent:side==='guest'?'flex-end':'flex-start', marginBottom:7, alignItems:'flex-end', gap:6 }}>
      {side==='ai' && (
        <div style={{ width:22,height:22,borderRadius:'50%',background:'linear-gradient(135deg,#4f46e5,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:900,color:'#fff',flexShrink:0 }}>S</div>
      )}
      <div style={{ background:side==='guest'?'linear-gradient(135deg,#4f46e5,#6366f1)':'rgba(255,255,255,.08)',
        color:'#e2e8f0', padding:'8px 13px', fontSize:12, lineHeight:1.5, maxWidth:'76%',
        borderRadius:side==='guest'?'14px 14px 4px 14px':'14px 14px 14px 4px',
        boxShadow:side==='guest'?'0 2px 8px rgba(79,70,229,.3)':'none' }}>
        {text}
      </div>
    </div>
  );
}
function STyping() {
  return (
    <div style={{ display:'flex', alignItems:'center', marginBottom:7, marginLeft:28 }}>
      <div style={{ display:'flex', gap:4, padding:'7px 11px', background:'rgba(255,255,255,.07)', borderRadius:'12px 12px 12px 4px' }}>
        {[0,1,2].map(i => <div key={i} style={{ width:5,height:5,borderRadius:'50%',background:'#475569',animation:`typingBounce 1.1s ${i*.18}s ease-in-out infinite` }} />)}
      </div>
    </div>
  );
}

/* ─── AI Chat Mockup ─────────────────────────────────────────────── */
function AIMockup({ active }) {
  const [phase, setPhase] = useState(0);
  const timers = useRef([]);
  useEffect(() => {
    timers.current.forEach(clearTimeout); timers.current = []; setPhase(0);
    if (!active) return;
    const t = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    t(()=>setPhase(1), 500);  t(()=>setPhase(2), 1100);
    t(()=>setPhase(3), 2300); t(()=>setPhase(4), 3100);
    t(()=>setPhase(5), 3800); t(()=>setPhase(6), 5000);
    t(()=>setPhase(7), 5900); t(()=>setPhase(8), 6600);
    t(()=>setPhase(9), 7700);
    return () => timers.current.forEach(clearTimeout);
  }, [active]);
  const vis = n => ({ opacity:phase>=n?1:0, transform:phase>=n?'none':'translateY(8px)', transition:'opacity .38s ease,transform .38s ease' });
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,.07)', background:'rgba(255,255,255,.02)' }}>
        <div style={{ width:30,height:30,borderRadius:'50%',background:'linear-gradient(135deg,#4f46e5,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:900,color:'#fff',flexShrink:0 }}>S</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#f1f5f9' }}>Seva AI Concierge</div>
          <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:1 }}>
            <div style={{ width:5,height:5,borderRadius:'50%',background:'#10b981' }} />
            <span style={{ fontSize:10, color:'#10b981', fontWeight:600 }}>Online now · replies instantly</span>
          </div>
        </div>
        <span style={{ fontSize:9, color:'#334155', fontWeight:600, background:'rgba(255,255,255,.05)', padding:'3px 9px', borderRadius:20 }}>WhatsApp · Web · SMS</span>
      </div>
      <div style={{ flex:1, padding:'14px', display:'flex', flexDirection:'column', justifyContent:'center' }}>
        <div style={vis(1)}><SChatBubble side="guest" text="Hi! I'd like to book a table for Saturday evening 🍷" /></div>
        <div style={{ opacity:phase===2?1:0, transition:'opacity .25s' }}><STyping /></div>
        <div style={vis(3)}><SChatBubble side="ai" text="Of course! 😊 How many guests will be joining you?" /></div>
        <div style={vis(4)}><SChatBubble side="guest" text="4 of us" /></div>
        <div style={{ opacity:phase===5?1:0, transition:'opacity .25s' }}><STyping /></div>
        <div style={vis(6)}><SChatBubble side="ai" text="Perfect! We have 7:30 PM and 8:00 PM free on Saturday — which works?" /></div>
        <div style={vis(7)}><SChatBubble side="guest" text="8pm please 🙌" /></div>
        <div style={{ opacity:phase===8?1:0, transition:'opacity .25s' }}><STyping /></div>
        <div style={vis(9)}><SChatBubble side="ai" text="✅ Confirmed! Table for 4 at 8:00 PM this Saturday. See you then — enjoy your evening!" /></div>
      </div>
      <div style={{ padding:'9px 13px', borderTop:'1px solid rgba(255,255,255,.06)', display:'flex', gap:8, alignItems:'center' }}>
        <div style={{ flex:1, background:'rgba(255,255,255,.04)', borderRadius:22, padding:'7px 14px', fontSize:11, color:'#1e293b' }}>Type a message...</div>
        <div style={{ width:28,height:28,borderRadius:'50%',background:'linear-gradient(135deg,#4f46e5,#6366f1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
        </div>
      </div>
    </div>
  );
}

/* ─── Floor Plan Mockup ──────────────────────────────────────────── */
const FLOOR_TABLES_V2 = [
  { id:'T1', cap:2, x:'4%',  y:22,  w:52, h:52, round:true,  status:'occupied'  },
  { id:'T2', cap:2, x:'4%',  y:118, w:52, h:52, round:true,  status:'available' },
  { id:'T3', cap:4, x:'20%', y:14,  w:66, h:66, round:false, status:'reserved'  },
  { id:'T4', cap:4, x:'20%', y:112, w:66, h:66, round:false, status:'occupied'  },
  { id:'T5', cap:4, x:'39%', y:14,  w:66, h:66, round:false, status:'available' },
  { id:'T6', cap:4, x:'39%', y:112, w:66, h:66, round:false, status:'available' },
  { id:'T7', cap:6, x:'59%', y:10,  w:90, h:62, round:false, status:'reserved'  },
  { id:'T8', cap:8, x:'59%', y:100, w:98, h:74, round:false, status:'occupied'  },
];
const SC_COL = { available:'#10b981', occupied:'#f59e0b', reserved:'#6366f1' };
const SC_BG  = { available:'rgba(16,185,129,.1)', occupied:'rgba(245,158,11,.1)', reserved:'rgba(99,102,241,.1)' };
const SC_BRD = { available:'rgba(16,185,129,.4)', occupied:'rgba(245,158,11,.4)', reserved:'rgba(99,102,241,.4)' };

function FloorMockup({ active }) {
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,.07)', gap:12 }}>
        <span style={{ fontSize:12, fontWeight:700, color:'#f1f5f9', flex:1 }}>Main Dining Room</span>
        <div style={{ display:'flex', gap:10 }}>
          {Object.entries(SC_COL).map(([s,c]) => (
            <span key={s} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#475569', fontWeight:600 }}>
              <span style={{ width:7,height:7,borderRadius:'50%',background:c,display:'inline-block' }} />
              {s.charAt(0).toUpperCase()+s.slice(1)}
            </span>
          ))}
        </div>
      </div>
      <div style={{ flex:1, position:'relative', margin:'12px 14px 14px', background:'rgba(8,14,30,.8)', borderRadius:12, border:'1px solid rgba(255,255,255,.07)', overflow:'hidden' }}>
        {/* Grid background */}
        <div style={{ position:'absolute',inset:0,backgroundImage:'linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)',backgroundSize:'28px 28px' }} />
        {/* Window-seats divider */}
        <div style={{ position:'absolute',left:'15%',top:0,bottom:0,width:'1px',background:'rgba(255,255,255,.05)' }} />
        <div style={{ position:'absolute',left:'2%',top:'50%',transform:'translateY(-50%) rotate(-90deg)',fontSize:8,color:'#1e293b',fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',whiteSpace:'nowrap',userSelect:'none' }}>WINDOW</div>
        {/* Tables */}
        {FLOOR_TABLES_V2.map((t,i) => (
          <div key={t.id} style={{
            position:'absolute', left:t.x, top:t.y, width:t.w, height:t.h,
            borderRadius:t.round?'50%':10,
            background:SC_BG[t.status],
            border:`2px solid ${SC_BRD[t.status]}`,
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            opacity:active?1:0, transform:active?'none':'scale(.8)',
            transition:`opacity .45s ${i*.05}s ease, transform .5s ${i*.05}s cubic-bezier(.34,1.56,.64,1)`,
            userSelect:'none',
          }}>
            <span style={{ fontSize:t.w>65?11:10, fontWeight:800, color:'#e2e8f0', lineHeight:1 }}>{t.id}</span>
            <span style={{ fontSize:9, color:SC_COL[t.status], fontWeight:700, marginTop:2 }}>{t.cap}p</span>
          </div>
        ))}
        <div style={{ position:'absolute',bottom:8,right:10,fontSize:9,color:'#475569',background:'rgba(0,0,0,.55)',padding:'4px 10px',borderRadius:20,fontWeight:600 }}>
          4 free · 3 seated · 2 reserved
        </div>
      </div>
    </div>
  );
}

/* ─── Reservations Dashboard ─────────────────────────────────────── */
const DRESV2 = [
  { init:'E', name:'Emma Taylor',  size:4, time:'7:00 PM', table:'T5', status:'confirmed' },
  { init:'J', name:'James Chen',   size:2, time:'7:30 PM', table:'T1', status:'seated'    },
  { init:'A', name:'Amara Osei',   size:6, time:'8:00 PM', table:'T7', status:'confirmed' },
  { init:'L', name:'Lucas Müller', size:3, time:'8:30 PM', table:'T3', status:'pending'   },
];
const DS_COL = { confirmed:'#10b981', seated:'#818cf8', pending:'#f59e0b' };
const DS_BG  = { confirmed:'rgba(16,185,129,.14)', seated:'rgba(129,140,248,.14)', pending:'rgba(245,158,11,.14)' };

function DashboardMockup({ active }) {
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,.07)' }}>
        <span style={{ fontSize:12, fontWeight:700, color:'#f1f5f9', flex:1 }}>Saturday · 19 Apr</span>
        <span style={{ fontSize:11, background:'rgba(99,102,241,.18)', color:'#a5b4fc', borderRadius:20, padding:'3px 10px', fontWeight:700 }}>13 bookings today</span>
      </div>
      <div style={{ display:'flex', gap:6, padding:'10px 12px' }}>
        {[['47','Total covers'],['8','Upcoming'],['4','Seated now'],['€2,840','Revenue est.']].map(([v,l],i) => (
          <div key={l} style={{ flex:1, background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.06)', borderRadius:9, padding:'8px 6px', textAlign:'center',
            opacity:active?1:0, transform:active?'none':'translateY(7px)',
            transition:`opacity .35s ${i*.07}s ease, transform .35s ${i*.07}s ease` }}>
            <div style={{ fontSize:16, fontWeight:900, color:i===3?'#10b981':'#818cf8', lineHeight:1 }}>{v}</div>
            <div style={{ fontSize:9, color:'#334155', marginTop:3, fontWeight:600, lineHeight:1.3 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ flex:1, padding:'2px 10px 10px' }}>
        {DRESV2.map((r,i) => (
          <div key={r.name} style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 10px',
            background:'rgba(255,255,255,.03)', borderRadius:9, marginBottom:6, border:'1px solid rgba(255,255,255,.05)',
            opacity:active?1:0, transform:active?'none':'translateX(-14px)',
            transition:`opacity .35s ${.2+i*.07}s ease, transform .35s ${.2+i*.07}s ease` }}>
            <div style={{ width:28,height:28,borderRadius:7,background:'rgba(99,102,241,.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'#818cf8',flexShrink:0 }}>{r.init}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#f1f5f9', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</div>
              <div style={{ fontSize:10, color:'#475569' }}>{r.size} guests · {r.time} · {r.table}</div>
            </div>
            <div style={{ fontSize:10,fontWeight:700,color:DS_COL[r.status],background:DS_BG[r.status],borderRadius:20,padding:'3px 9px',flexShrink:0,textTransform:'capitalize' }}>{r.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Analytics Mockup ───────────────────────────────────────────── */
const ABARS2 = [{d:'Mon',n:28},{d:'Tue',n:35},{d:'Wed',n:43},{d:'Thu',n:58},{d:'Fri',n:74},{d:'Sat',n:94},{d:'Sun',n:62}];

function AnalyticsMockup({ active }) {
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,.07)' }}>
        <span style={{ fontSize:12, fontWeight:700, color:'#f1f5f9', flex:1 }}>Covers this week</span>
        <span style={{ fontSize:11, color:'#10b981', fontWeight:700, background:'rgba(16,185,129,.1)', borderRadius:20, padding:'3px 10px' }}>↑ 12% vs last week</span>
      </div>
      <div style={{ display:'flex', gap:6, padding:'10px 12px' }}>
        {[['394','Total covers'],['67','Bookings'],['3.4','Avg party']].map(([v,l],i) => (
          <div key={l} style={{ flex:1, background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.06)', borderRadius:9, padding:'8px 6px', textAlign:'center',
            opacity:active?1:0, transition:`opacity .35s ${i*.08}s ease` }}>
            <div style={{ fontSize:16, fontWeight:900, color:'#818cf8' }}>{v}</div>
            <div style={{ fontSize:9, color:'#334155', marginTop:3, fontWeight:600 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ flex:1, display:'flex', alignItems:'flex-end', gap:5, padding:'8px 14px 4px' }}>
        {ABARS2.map((b,i) => (
          <div key={b.d} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <span style={{ fontSize:9, color:'#475569', opacity:active?1:0, transition:`opacity .35s ${i*.07}s` }}>{b.n}</span>
            <div style={{ width:'100%', borderRadius:'4px 4px 0 0',
              background:b.d==='Sat'?'linear-gradient(to top,#4f46e5,#818cf8)':'linear-gradient(to top,#1e1b4b,#4338ca)',
              height:active?`${(b.n/94)*100}px`:'3px',
              transition:`height .75s ${i*.07}s cubic-bezier(.34,1.56,.64,1)`,
              boxShadow:b.d==='Sat'?'0 0 14px rgba(99,102,241,.5)':'none' }} />
            <span style={{ fontSize:9, color:b.d==='Sat'?'#818cf8':'#475569', fontWeight:b.d==='Sat'?700:500 }}>{b.d}</span>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:6, padding:'6px 12px 12px' }}>
        {[['Peak hour','8:00 PM'],['Busiest day','Saturday'],['Conversion','94%']].map(([l,v]) => (
          <div key={l} style={{ flex:1, textAlign:'center', background:'rgba(99,102,241,.06)', border:'1px solid rgba(99,102,241,.14)', borderRadius:8, padding:'7px 6px',
            opacity:active?1:0, transition:'opacity .5s .4s ease' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#a5b4fc' }}>{v}</div>
            <div style={{ fontSize:9, color:'#334155', marginTop:2, fontWeight:500 }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Waitlist Mockup ────────────────────────────────────────────── */
const DWAIT2 = [
  { name:'Maria Santos', size:3, waited:'2 min'  },
  { name:'David Kim',    size:5, waited:'8 min'  },
  { name:'Priya Ramesh', size:2, waited:'14 min' },
];

function WaitlistMockup({ active }) {
  const [notified, setNotified] = useState(null);
  useEffect(() => { if (!active) { setNotified(null); return; } const t = setTimeout(()=>setNotified(0),2800); return ()=>clearTimeout(t); }, [active]);
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,.07)' }}>
        <span style={{ fontSize:12, fontWeight:700, color:'#f1f5f9', flex:1 }}>Waitlist</span>
        <span style={{ fontSize:11, color:'#f59e0b', fontWeight:700, background:'rgba(245,158,11,.1)', borderRadius:20, padding:'3px 10px' }}>3 waiting</span>
      </div>
      <div style={{ margin:'10px 14px 0', padding:'9px 12px', background:'rgba(16,185,129,.07)', border:'1px solid rgba(16,185,129,.2)', borderRadius:9, display:'flex', alignItems:'center', gap:8, opacity:active?1:0, transition:'opacity .5s .3s ease' }}>
        <div style={{ width:6,height:6,borderRadius:'50%',background:'#10b981',flexShrink:0 }} />
        <span style={{ fontSize:11, color:'#10b981', fontWeight:700 }}>Table T2 just freed up</span>
        <span style={{ fontSize:10, color:'#065f46', marginLeft:'auto', fontWeight:600 }}>Notify next guest →</span>
      </div>
      <div style={{ flex:1, padding:'10px 14px', display:'flex', flexDirection:'column', gap:7 }}>
        {DWAIT2.map((w,i) => (
          <div key={w.name} style={{ display:'flex', alignItems:'center', gap:9, padding:'10px 12px',
            background:notified===i?'rgba(16,185,129,.07)':'rgba(255,255,255,.03)',
            border:`1px solid ${notified===i?'rgba(16,185,129,.25)':'rgba(255,255,255,.05)'}`,
            borderRadius:10, transition:'all .4s ease',
            opacity:active?1:0, transform:active?'none':'translateY(8px)',
            transitionDelay:active?`${i*.1}s`:'0s' }}>
            <div style={{ width:28,height:28,borderRadius:'50%',background:'rgba(245,158,11,.16)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'#f59e0b',flexShrink:0 }}>{i+1}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#f1f5f9' }}>{w.name}</div>
              <div style={{ fontSize:10, color:'#475569' }}>{w.size} guests · waiting {w.waited}</div>
            </div>
            <button onClick={()=>setNotified(i)} style={{ fontSize:10,fontWeight:700,color:notified===i?'#fff':'#10b981',background:notified===i?'#10b981':'rgba(16,185,129,.1)',border:`1px solid ${notified===i?'#10b981':'rgba(16,185,129,.25)'}`,borderRadius:7,padding:'5px 11px',cursor:'pointer',transition:'all .3s',flexShrink:0 }}>
              {notified===i?'✓ Notified':'Notify'}
            </button>
          </div>
        ))}
      </div>
      <div style={{ padding:'0 14px 12px', fontSize:10, color:'#1e293b', textAlign:'center', lineHeight:1.5, opacity:active?1:0, transition:'opacity .5s .5s ease' }}>
        Seva auto-notifies guests the moment their table is ready
      </div>
    </div>
  );
}

/* ─── Feature Showcase ───────────────────────────────────────────── */
const SI = {
  ai:        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  dashboard: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  floorplan: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  analytics: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  waitlist:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
};
const SHOWCASE_ITEMS = [
  { id:'ai',        label:'AI Booking Agent',       desc:'24/7 reservations in any language'  },
  { id:'dashboard', label:'Reservations Dashboard', desc:'Real-time overview of all bookings' },
  { id:'floorplan', label:'Live Floor Plan',         desc:'Table status at a glance'           },
  { id:'analytics', label:'Analytics',               desc:'Covers, trends & peak hours'        },
  { id:'waitlist',  label:'Waitlist',                desc:"Never lose a guest when you're full"},
];
const SHOWCASE_IDS = SHOWCASE_ITEMS.map(s => s.id);

function FeatureShowcase() {
  const [active, setActive] = useState('ai');
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(()=>setActive(c=>SHOWCASE_IDS[(SHOWCASE_IDS.indexOf(c)+1)%SHOWCASE_IDS.length]), 4500);
    return ()=>clearInterval(t);
  }, [paused]);
  const select = id => { setActive(id); setPaused(true); };
  const renderMockup = id => {
    switch(id) {
      case 'ai':        return <AIMockup active={active==='ai'} />;
      case 'dashboard': return <DashboardMockup active={active==='dashboard'} />;
      case 'floorplan': return <FloorMockup active={active==='floorplan'} />;
      case 'analytics': return <AnalyticsMockup active={active==='analytics'} />;
      case 'waitlist':  return <WaitlistMockup active={active==='waitlist'} />;
      default: return null;
    }
  };
  return (
    <div style={{ display:'flex', gap:18, alignItems:'stretch' }}>
      {/* Left nav */}
      <div style={{ width:210, flexShrink:0, display:'flex', flexDirection:'column', gap:3 }}>
        {SHOWCASE_ITEMS.map(item => {
          const on = active===item.id;
          return (
            <button key={item.id} onClick={()=>select(item.id)} style={{
              display:'flex', alignItems:'center', gap:9, padding:'11px 13px', borderRadius:12, textAlign:'left',
              border:`1px solid ${on?'rgba(99,102,241,.45)':'rgba(255,255,255,.05)'}`,
              background:on?'rgba(99,102,241,.1)':'rgba(255,255,255,.02)',
              cursor:'pointer', transition:'all .22s', position:'relative', overflow:'hidden' }}>
              {on && <div style={{ position:'absolute',left:0,top:'18%',bottom:'18%',width:3,background:'#6366f1',borderRadius:'0 2px 2px 0' }} />}
              <span style={{ color:on?'#818cf8':'#334155', flexShrink:0, transition:'color .2s' }}>{SI[item.id]}</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:on?'#c7d2fe':'#64748b', transition:'color .2s' }}>{item.label}</div>
                <div style={{ fontSize:10, color:on?'#4338ca':'#1e293b', marginTop:1, lineHeight:1.3, transition:'color .2s' }}>{item.desc}</div>
              </div>
            </button>
          );
        })}
        <div style={{ marginTop:6, padding:'10px 13px', background:'rgba(99,102,241,.06)', border:'1px solid rgba(99,102,241,.12)', borderRadius:12 }}>
          <div style={{ fontSize:10, fontWeight:800, color:'#6366f1', marginBottom:3, letterSpacing:'.04em', textTransform:'uppercase' }}>All included</div>
          <div style={{ fontSize:10, color:'#334155', lineHeight:1.55 }}>Every feature on all Seva plans. No add-ons, no hidden fees.</div>
        </div>
      </div>
      {/* App window */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', alignItems:'center', gap:7, padding:'10px 16px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'18px 18px 0 0' }}>
          {['#ff5f56','#ffbd2e','#27c93f'].map(c=><div key={c} style={{ width:10,height:10,borderRadius:'50%',background:c,opacity:.75 }}/>)}
          <div style={{ flex:1, textAlign:'center', fontSize:11, fontWeight:600, color:'#334155' }}>
            {SHOWCASE_ITEMS.find(i=>i.id===active)?.label}
          </div>
          {!paused && <span style={{ fontSize:9, color:'#1e293b', fontWeight:600 }}>auto-playing</span>}
        </div>
        <div style={{ flex:1, background:'rgba(10,14,26,.95)', border:'1px solid rgba(255,255,255,.08)', borderTop:'none', borderRadius:'0 0 18px 18px', position:'relative', minHeight:360, overflow:'hidden' }}>
          {SHOWCASE_IDS.map(id=>(
            <div key={id} style={{ position:'absolute', inset:0,
              opacity:active===id?1:0, transform:active===id?'none':'scale(.98) translateY(8px)',
              transition:'opacity .4s ease, transform .4s ease',
              pointerEvents:active===id?'auto':'none' }}>
              {renderMockup(id)}
            </div>
          ))}
          {!paused && (
            <div style={{ position:'absolute',bottom:0,left:0,right:0,height:2,background:'rgba(255,255,255,.04)' }}>
              <div key={active} style={{ height:'100%', background:'linear-gradient(90deg,#4f46e5,#818cf8)', animation:'lpProgress 4.5s linear forwards' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function LandingPage({ onSignIn, onApply }) {
  const [statsTriggered, setStatsTriggered] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const el = statsRef.current;
    if (!el || statsTriggered) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStatsTriggered(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [statsTriggered]);

  useEffect(() => {
    // Always refresh the injected styles
    const id = 'seva-lp-styles';
    const old = document.getElementById(id);
    if (old) old.remove();
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
      @keyframes heroFadeUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
      @keyframes floatBob    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
      @keyframes gradShift   { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
      @keyframes chatSlideR  { from{opacity:0;transform:translateX(18px) scale(.97)} to{opacity:1;transform:none} }
      @keyframes chatSlideL  { from{opacity:0;transform:translateX(-18px) scale(.97)} to{opacity:1;transform:none} }
      @keyframes typingBounce{ 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }

      /* Standard fade-up */
      .lp-rv { opacity:0; transform:translateY(28px); transition:opacity .6s cubic-bezier(.22,1,.36,1),transform .6s cubic-bezier(.22,1,.36,1); }
      .lp-rv.lp-vis { opacity:1; transform:none; }

      /* Spring scale pop — steps, features, pricing cards */
      .lp-sp { opacity:0; transform:translateY(26px) scale(0.88); transition:opacity .5s ease,transform .65s cubic-bezier(.34,1.56,.64,1); }
      .lp-sp.lp-vis { opacity:1; transform:none; }

      /* Slide from left — compare rows */
      .lp-sl { opacity:0; transform:translateX(-22px); transition:opacity .4s ease,transform .4s cubic-bezier(.22,1,.36,1); }
      .lp-sl.lp-vis { opacity:1; transform:none; }

      /* Stagger delays */
      .lp-d0{transition-delay:0ms}   .lp-d1{transition-delay:80ms}  .lp-d2{transition-delay:160ms}
      .lp-d3{transition-delay:240ms} .lp-d4{transition-delay:320ms} .lp-d5{transition-delay:400ms}
      .lp-d6{transition-delay:480ms} .lp-d7{transition-delay:560ms}

      /* Hover */
      .lp-btn-p:hover { transform:translateY(-2px); box-shadow:0 10px 28px rgba(79,70,229,.45)!important; }
      .lp-btn-g:hover { background:rgba(255,255,255,.1)!important; }
      .lp-feat:hover  { transform:translateY(-3px)!important; background:rgba(255,255,255,.07)!important; }
      .lp-plan:hover  { transform:translateY(-4px)!important; }
      .lp-nav-a:hover { color:rgba(255,255,255,.85)!important; }
      html { scroll-behavior:smooth; }
      @keyframes lpProgress { from{width:0%} to{width:100%} }
    `;
    document.head.appendChild(el);

    // Bidirectional observer: add lp-vis on enter, remove on exit so animation replays
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.style.transitionDelay = '';   // let CSS stagger classes take over
          e.target.classList.add('lp-vis');
        } else {
          e.target.style.transitionDelay = '0ms'; // exit instantly (no reverse stagger)
          e.target.classList.remove('lp-vis');
        }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.lp-rv, .lp-sp, .lp-sl').forEach(node => obs.observe(node));
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{ position:'fixed', inset:0, overflowY:'scroll', overflowX:'hidden', zIndex:100 }}>
    <div style={{ fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', overflowX:'hidden', background:'#0f172a' }}>

      {/* ══ NAV ══ */}
      <nav style={{ position:'sticky', top:0, zIndex:200, height:64, padding:'0 5%',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'rgba(15,23,42,0.85)',
        backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)',
        borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:9, overflow:'hidden', flexShrink:0 }}>
            <img src="/icon-192.png" alt="Seva" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          </div>
          <span style={{ fontSize:18, fontWeight:800, color:'#f1f5f9', letterSpacing:'-0.3px' }}>Seva</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {[['#how','How it works'],['#features','Features'],['#pricing','Pricing']].map(([href, label]) => (
            <a key={href} href={href} className="lp-nav-a" style={{ color:'rgba(255,255,255,.4)', fontSize:14, fontWeight:500, textDecoration:'none', padding:'6px 12px', transition:'color .15s' }}>{label}</a>
          ))}
          <div style={{ width:1, height:20, background:'rgba(255,255,255,.08)', margin:'0 4px' }} />
          <button onClick={onSignIn} className="lp-btn-g" style={{ background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, padding:'7px 16px', color:'#e2e8f0', fontSize:14, fontWeight:600, cursor:'pointer', transition:'background .15s' }}>Sign in</button>
          <button onClick={onApply} className="lp-btn-p" style={{ background:'#4f46e5', border:'none', borderRadius:8, padding:'7px 16px', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', transition:'transform .2s,box-shadow .2s' }}>Apply now</button>
        </div>
      </nav>

      {/* ══ HERO ══ */}
      <section style={{ minHeight:'100vh', paddingTop:64, background:'#0f172a',
        backgroundImage:'radial-gradient(ellipse 90% 60% at 50% 0%,rgba(79,70,229,.28) 0%,transparent 65%)',
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        textAlign:'center', position:'relative', overflow:'hidden', padding:'120px 5% 60px' }}>
        <div style={{ position:'absolute', inset:0, pointerEvents:'none',
          backgroundImage:'linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)',
          backgroundSize:'48px 48px',
          maskImage:'radial-gradient(ellipse 70% 70% at 50% 40%,black 0%,transparent 100%)',
          WebkitMaskImage:'radial-gradient(ellipse 70% 70% at 50% 40%,black 0%,transparent 100%)' }} />
        <div style={{ position:'relative', maxWidth:800, animation:'heroFadeUp .9s cubic-bezier(.22,1,.36,1) both' }}>
          {/* Online badge */}
          <div style={{ display:'inline-flex', alignItems:'center', gap:7, background:'rgba(16,185,129,.1)', border:'1px solid rgba(16,185,129,.25)', borderRadius:24, padding:'5px 14px', marginBottom:32, fontSize:12, fontWeight:700, color:'#10b981' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:'#10b981', boxShadow:'0 0 0 3px rgba(16,185,129,.2)' }} />
            Online now · replies instantly
          </div>
          {/* Main headline — Seva introduces itself */}
          <h1 style={{ fontSize:'clamp(32px,6vw,68px)', fontWeight:900, lineHeight:1.1, letterSpacing:'-2px', color:'#f8fafc', margin:'0 0 28px' }}>
            Hi, I'm{' '}
            <span style={{ background:'linear-gradient(135deg,#818cf8 0%,#6366f1 40%,#a78bfa 100%)', backgroundSize:'200% 200%', animation:'gradShift 5s ease infinite', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Seva</span>
            {' '}— your restaurant's personal AI booking assistant. 👋
          </h1>
          <p style={{ fontSize:'clamp(16px,2.2vw,19px)', color:'#64748b', lineHeight:1.75, margin:'0 auto 44px', maxWidth:560 }}>
            Seva's AI answers instantly — in their language, at any hour. It books the table, combines seats for large groups, handles special requests, and sends the confirmation. You wake up to a full diary.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={onApply} className="lp-btn-p" style={{ background:'#4f46e5', border:'none', borderRadius:12, padding:'15px 36px', fontSize:16, fontWeight:700, color:'#fff', cursor:'pointer', transition:'transform .2s,box-shadow .2s' }}>Stop losing bookings →</button>
            <button className="lp-btn-g" onClick={onSignIn} style={{ background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.12)', borderRadius:12, padding:'15px 36px', fontSize:16, fontWeight:600, color:'#e2e8f0', cursor:'pointer', transition:'background .2s' }}>Sign in to dashboard</button>
          </div>
          <p style={{ fontSize:12, color:'#334155', marginTop:16 }}>No per-cover fees · No long-term contract · Set up in under 30 minutes</p>
        </div>

        {/* Chat widget with typing simulation */}
        <ChatWidget />
      </section>

      {/* ══ WHILE YOU SLEPT ══ */}
      <NightFeedSection />

      {/* ══ STATS BAR ══ */}
      <div ref={statsRef} style={{ background:'rgba(255,255,255,.025)', borderTop:'1px solid rgba(255,255,255,.06)', borderBottom:'1px solid rgba(255,255,255,.06)', display:'flex', justifyContent:'center', flexWrap:'wrap', gap:'6%', padding:'36px 5%' }}>
        {STATS_DATA.map(s => (
          <div key={s.label} className="lp-rv" style={{ textAlign:'center', minWidth:100 }}>
            <div style={{ fontSize:30, fontWeight:900, color:'#6366f1', letterSpacing:'-1px' }}>
              <CountUp prefix={s.prefix} to={s.to} suffix={s.suffix} triggered={statsTriggered} />
            </div>
            <div style={{ fontSize:13, color:'#475569', marginTop:4, fontWeight:500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ══ WAVE: dark → white ══ */}
      <Wave from="#0f172a" to="#ffffff" />

      {/* ══ HOW IT WORKS ══ */}
      <section id="how" style={{ background:'#ffffff', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:1080, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:64 }}>
            <Pill light>How it works</Pill>
            <H2 dark>From chat to confirmed — in minutes</H2>
            <Sub dark>Your AI handles the entire booking journey end-to-end. No phone tag, no missed bookings.</Sub>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(270px,1fr))', gap:28 }}>
            {STEPS.map((step, i) => (
              <div key={i} className={`lp-sp lp-d${i}`}
                style={{ background:'#f8fafc', borderRadius:20, padding:'32px 30px',
                  border:'1px solid #e2e8f0', position:'relative', overflow:'hidden',
                  transition:'transform .25s,box-shadow .25s' }}>
                <div style={{ position:'absolute', top:20, right:24, fontSize:48, fontWeight:900, color:'#f1f5f9', lineHeight:1, userSelect:'none' }}>{step.n}</div>
                <div style={{ fontSize:13, fontWeight:800, color:'#4f46e5', letterSpacing:'.08em', marginBottom:14, fontFamily:'monospace' }}>{step.n}</div>
                <h3 style={{ fontSize:20, fontWeight:800, color:'#0f172a', margin:'0 0 12px', lineHeight:1.3 }}>{step.title}</h3>
                <p style={{ fontSize:15, color:'#64748b', lineHeight:1.7, margin:0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WAVE: white → dark ══ */}
      <Wave from="#ffffff" to="#0f172a" flip />

      {/* ══ FEATURES ══ */}
      <section id="features" style={{ background:'#0f172a', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:1080, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:64 }}>
            <Pill dark>Everything you need</Pill>
            <H2 light>Built for modern restaurants</H2>
            <Sub light>One platform. AI booking agent, staff dashboard, floor plans, analytics, and more.</Sub>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(230px,1fr))', gap:18 }}>
            {FEATURES.map((f, i) => (
              <div key={i} className={`lp-sp lp-feat lp-d${i % 6}`}
                style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)',
                  borderRadius:16, padding:'26px 24px',
                  transition:'transform .25s cubic-bezier(.34,1.56,.64,1),background .2s' }}>
                <Icon>{f.icon}</Icon>
                <h3 style={{ fontSize:15, fontWeight:700, color:'#f1f5f9', margin:'0 0 8px' }}>{f.title}</h3>
                <p style={{ fontSize:13, color:'#64748b', lineHeight:1.65, margin:0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WAVE: dark → white ══ */}
      <Wave from="#0f172a" to="#ffffff" />

      {/* ══ SCENARIOS — where basic tools fail ══ */}
      <section style={{ background:'#ffffff', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:1080, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:16 }}>
            <Pill light>Seva vs. basic booking tools</Pill>
            <H2 dark>Not just a booking widget.</H2>
          </div>
          <p className="lp-rv" style={{ fontSize:18, color:'#64748b', lineHeight:1.65, textAlign:'center', maxWidth:580, margin:'0 auto 64px' }}>
            Every competitor promises "24/7 bookings." Here's what actually happens when things get real.
          </p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(460px,1fr))', gap:22 }}>
            {SCENARIOS.map((s, i) => (
              <div key={i} className={`lp-sp lp-d${i}`}
                style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:20, overflow:'hidden',
                  transition:'transform .25s,box-shadow .25s' }}>
                {/* Scenario label */}
                <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:10, background:s.colorBg, border:`1px solid ${s.colorBorder}`, display:'flex', alignItems:'center', justifyContent:'center', color:s.color, flexShrink:0 }}>
                    {s.icon}
                  </div>
                  <span style={{ fontSize:15, fontWeight:700, color:'#0f172a', lineHeight:1.35 }}>{s.scenario}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr' }}>
                  {/* Them */}
                  <div style={{ padding:'18px 20px', borderRight:'1px solid #f1f5f9' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'#fef2f2', border:'1px solid #fecaca', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.06em' }}>{s.them.label}</span>
                    </div>
                    <p style={{ fontSize:13, color:'#64748b', lineHeight:1.65, margin:0 }}>{s.them.text}</p>
                  </div>
                  {/* Seva */}
                  <div style={{ padding:'18px 20px', background:'rgba(79,70,229,.02)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'#f0fdf4', border:'1px solid #bbf7d0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:'#6366f1', textTransform:'uppercase', letterSpacing:'.06em' }}>Seva</span>
                    </div>
                    <p style={{ fontSize:13, color:'#374151', lineHeight:1.65, margin:0, fontWeight:500 }}>{s.seva.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WAVE: white → light grey ══ */}
      <Wave from="#ffffff" to="#f8fafc" />

      {/* ══ WHY SEVA ══ */}
      <section style={{ background:'#f8fafc', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:860, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:64 }}>
            <Pill green>Full comparison</Pill>
            <H2 dark>Seva vs. the rest — at a glance</H2>
            <Sub dark>No per-cover fees, no phone calls for large groups, no lost guests when you're fully booked.</Sub>
          </div>
          <div className="lp-rv" style={{ background:'#fff', borderRadius:20, overflow:'hidden', border:'1px solid #e2e8f0', boxShadow:'0 4px 24px rgba(15,23,42,.07)' }}>
            {/* Table header */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', background:'#0f172a', padding:'16px 28px' }}>
              {['','Seva','Basic widget'].map((h, i) => (
                <div key={i} style={{ fontSize:11, fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color: i===1 ? '#a5b4fc' : '#475569' }}>{h}</div>
              ))}
            </div>
            {/* Rows slide in from left with stagger */}
            {COMPARE.map((row, i) => (
              <div key={i} className={`lp-sl lp-d${i}`}
                style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', padding:'15px 28px',
                  borderTop:'1px solid #f1f5f9', background: i%2===0 ? '#fff' : '#fafbfc' }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#475569' }}>{row.label}</div>
                <div style={{ fontSize:14, color:'#15803d', fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ color:'#10b981' }}>✓</span>{row.seva}
                </div>
                <div style={{ fontSize:14, color:'#94a3b8' }}>{row.them}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WAVE: light grey → dark ══ */}
      <Wave from="#f8fafc" to="#0f172a" flip />

      {/* ══ FAQ ══ */}
      <section style={{ background:'#0f172a', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:720, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:56 }}>
            <Pill dark>FAQ</Pill>
            <H2 light>Answers before you have to ask</H2>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {[
              {
                q: 'What if the AI makes a mistake?',
                a: 'Every booking lands in your staff dashboard instantly — you have full visibility and can edit, cancel or move anything. The AI is also trained to flag ambiguous requests rather than guess, so your team always stays in control.',
              },
              {
                q: 'Can I still take bookings manually?',
                a: 'Absolutely. The staff dashboard lets your team create, edit and cancel reservations at any time. Seva handles the guest-facing side; you keep full control behind the scenes.',
              },
              {
                q: 'Will it work with my existing website?',
                a: "Yes — Seva is a widget you embed with one line of code. It works on WordPress, Wix, Squarespace, Shopify, and any custom-built site. No rebuild required.",
              },
              {
                q: 'What if a guest calls instead of using the widget?',
                a: "Your phone line works exactly as it always has — nothing changes there. Seva captures the bookings your phone misses: after hours, peak-time, international guests who don't want to call.",
              },
              {
                q: "What happens when we're fully booked?",
                a: "Seva first suggests the next available date and time that works. If the guest can't make any of those, it offers a waitlist spot — and the moment a cancellation comes in, they're notified automatically. No manual chasing, no lost guests.",
              },
              {
                q: 'How long does setup take?',
                a: 'Under 30 minutes for the self-serve setup: add your tables, set your hours, paste the widget code. If you want us to handle everything — tables, floor plan, AI tone, the works — our onboarding team can do it for you.',
              },
            ].map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ WAVE: dark → very dark ══ */}
      <Wave from="#0f172a" to="#0a0e1a" />

      {/* ══ PRICING ══ */}
      <section id="pricing" style={{ background:'#0a0e1a', padding:'80px 5% 100px' }}>
        <div style={{ maxWidth:920, margin:'0 auto' }}>
          <div className="lp-rv" style={{ textAlign:'center', marginBottom:64 }}>
            <Pill dark>Pricing</Pill>
            <H2 light>Simple, honest pricing</H2>
            <Sub light>No per-cover fees. No hidden charges. Cancel anytime.</Sub>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:24, marginBottom:24 }}>

            {/* Plan 1 — Seva Core */}
            <div className="lp-sp lp-plan lp-d0"
              style={{ background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)',
                borderRadius:22, padding:'32px 30px',
                transition:'transform .3s cubic-bezier(.34,1.56,.64,1)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
                <div style={{ width:36, height:36, background:'rgba(99,102,241,.15)', border:'1px solid rgba(99,102,241,.25)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color:'#f1f5f9' }}>Seva Core</div>
                  <div style={{ fontSize:12, color:'#475569' }}>AI booking, fully managed</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:8 }}>
                <span style={{ fontSize:46, fontWeight:900, letterSpacing:'-2px', color:'#f1f5f9' }}>€75</span>
                <span style={{ fontSize:14, color:'#475569' }}>/month</span>
              </div>
              <p style={{ fontSize:13, color:'#64748b', lineHeight:1.65, marginBottom:26 }}>
                Everything you need to start taking AI-powered bookings today — set up directly on your existing website.
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:11, marginBottom:28 }}>
                {['AI booking chatbot on your website','Unlimited reservations','Full staff dashboard','Multi-language support (20+ languages)','Real-time availability & zero double-bookings','Automated email confirmations','Analytics, floor plan & waitlist tools','Widget embedded on your existing site'].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:14, color:'#e2e8f0' }}>
                    <span style={{ color:'#10b981', fontWeight:700, flexShrink:0, marginTop:1 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={onApply} style={{ width:'100%', padding:'13px', borderRadius:11, fontSize:14, fontWeight:700, cursor:'pointer', background:'rgba(255,255,255,.09)', color:'#f1f5f9', border:'1px solid rgba(255,255,255,.1)', transition:'opacity .15s' }}>
                Apply now
              </button>
            </div>

            {/* Plan 2 — Seva Launch */}
            <div className="lp-sp lp-plan lp-d1"
              style={{ background:'#4f46e5', border:'1px solid #6366f1', borderRadius:22, padding:'32px 30px',
                transition:'transform .3s cubic-bezier(.34,1.56,.64,1)', position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:18, right:18, background:'rgba(255,255,255,.2)', borderRadius:20, padding:'3px 11px', fontSize:11, fontWeight:700, color:'#fff' }}>Most popular</div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
                <div style={{ width:36, height:36, background:'rgba(255,255,255,.15)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color:'#fff' }}>Seva Launch</div>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,.6)' }}>AI booking + website built for you</div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:4 }}>
                <span style={{ fontSize:46, fontWeight:900, letterSpacing:'-2px', color:'#fff' }}>€75</span>
                <span style={{ fontSize:14, color:'rgba(255,255,255,.6)' }}>/month</span>
              </div>
              <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.15)', borderRadius:8, padding:'5px 12px', marginBottom:16 }}>
                <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>+ €99 one-time</span>
                <span style={{ fontSize:11, color:'rgba(255,255,255,.6)', textDecoration:'line-through' }}>€141</span>
                <span style={{ fontSize:11, fontWeight:700, color:'#a5f3c0', background:'rgba(16,185,129,.25)', borderRadius:6, padding:'1px 6px' }}>30% OFF</span>
              </div>
              <p style={{ fontSize:13, color:'rgba(255,255,255,.7)', lineHeight:1.65, marginBottom:26 }}>
                Everything in Seva Core, plus we build a professional restaurant website for you — with the booking widget pre-integrated and ready to go.
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:11, marginBottom:28 }}>
                {['Everything in Seva Core','Professional restaurant website built for you','Mobile-optimised & fast','Booking widget pre-integrated','Custom branding & design','Launch-ready in days, not weeks'].map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'flex-start', gap:10, fontSize:14, color:'rgba(255,255,255,.88)' }}>
                    <span style={{ color:'#c7d2fe', fontWeight:700, flexShrink:0, marginTop:1 }}>✓</span>{f}
                  </div>
                ))}
              </div>
              <button onClick={onApply} style={{ width:'100%', padding:'13px', borderRadius:11, fontSize:14, fontWeight:700, cursor:'pointer', background:'#fff', color:'#4f46e5', border:'none', transition:'opacity .15s' }}>
                Apply now
              </button>
            </div>
          </div>

          {/* Professional onboarding add-on */}
          <div className="lp-rv" style={{ background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderRadius:16, padding:'24px 28px', display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
            <div style={{ width:40, height:40, background:'rgba(16,185,129,.12)', border:'1px solid rgba(16,185,129,.2)', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            </div>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#f1f5f9', marginBottom:4 }}>Add professional onboarding — available on both plans</div>
              <div style={{ fontSize:13, color:'#64748b', lineHeight:1.6 }}>
                Let our team configure your entire restaurant in the app — tables, floor plan, hours, AI personality, and more. Pricing is tailored to your venue size. <span style={{ color:'#a5b4fc', fontWeight:600 }}>Get a free quote when you apply.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WAVE: very dark → dark ══ */}
      <Wave from="#0a0e1a" to="#0f172a" />

      {/* ══ FINAL CTA ══ */}
      <section style={{ background:'#0f172a', backgroundImage:'radial-gradient(ellipse 80% 60% at 50% 100%,rgba(79,70,229,.22) 0%,transparent 70%)', borderTop:'1px solid rgba(255,255,255,.05)', padding:'120px 5%', textAlign:'center' }}>
        <div className="lp-rv" style={{ maxWidth:620, margin:'0 auto' }}>
          <h2 style={{ fontSize:'clamp(28px,5vw,52px)', fontWeight:900, color:'#f8fafc', margin:'0 0 20px', letterSpacing:'-1.5px', lineHeight:1.15 }}>
            Ready to transform your restaurant?
          </h2>
          <p style={{ fontSize:18, color:'#64748b', lineHeight:1.7, margin:'0 0 44px' }}>
            Join forward-thinking restaurants already using Seva to deliver exceptional guest experiences — day and night, without the extra workload.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={onApply} className="lp-btn-p" style={{ background:'#4f46e5', border:'none', borderRadius:12, padding:'16px 38px', fontSize:16, fontWeight:700, color:'#fff', cursor:'pointer', transition:'transform .2s,box-shadow .2s' }}>Apply for early access</button>
            <button className="lp-btn-g" onClick={onSignIn} style={{ background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:12, padding:'16px 38px', fontSize:16, fontWeight:600, color:'#e2e8f0', cursor:'pointer', transition:'background .2s' }}>Already a customer →</button>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ background:'#060a12', borderTop:'1px solid rgba(255,255,255,.04)', padding:'28px 5%', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:26, height:26, borderRadius:7, overflow:'hidden', flexShrink:0 }}>
            <img src="/icon-192.png" alt="Seva" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          </div>
          <span style={{ fontSize:14, fontWeight:700, color:'#334155' }}>Seva</span>
        </div>
        <div style={{ fontSize:13, color:'#1e293b' }}>© 2025 Seva. All rights reserved.</div>
      </footer>
    </div>
    </div>
  );
}

/* ─── Night feed data ────────────────────────────────────────────── */
const NIGHT_EVENTS = [
  { time:'11:47 PM', emoji:'👥', label:'Party of 8 booked',        detail:'Tables T3 + T4 combined automatically',          color:'#818cf8', bg:'rgba(129,140,248,.12)' },
  { time:'12:23 AM', emoji:'💑', label:'Anniversary dinner',        detail:'Window table assigned · Note left for staff',    color:'#f472b6', bg:'rgba(244,114,182,.12)' },
  { time:'2:15 AM',  emoji:'🌍', label:'Booking in French',         detail:'Replied in French · Saturday 8:00 PM confirmed', color:'#38bdf8', bg:'rgba(56,189,248,.12)'  },
  { time:'3:40 AM',  emoji:'📋', label:'Full tonight — waitlisted', detail:'Guest added · Will be notified on cancellation',  color:'#fbbf24', bg:'rgba(251,191,36,.12)'  },
  { time:'5:12 AM',  emoji:'🥂', label:'Birthday party of 12',      detail:'3 tables merged · Champagne request noted',       color:'#a78bfa', bg:'rgba(167,139,250,.12)' },
];
const NIGHT_FINAL = { time:'7:00 AM', emoji:'☀️', label:'You wake up', detail:'5 new bookings confirmed · Special requests noted · Dashboard ready', color:'#34d399', bg:'rgba(52,211,153,.15)' };

function NightFeedSection() {
  const [count, setCount]   = useState(0);
  const [showEnd, setShowEnd] = useState(false);
  const ref    = useRef(null);
  const timers = useRef([]);
  const played = useRef(false);

  const run = useCallback(() => {
    if (played.current) return;
    played.current = true;
    NIGHT_EVENTS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setCount(i + 1), 400 + i * 950));
    });
    timers.current.push(setTimeout(() => setShowEnd(true), 400 + NIGHT_EVENTS.length * 950 + 300));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) run(); }, { threshold: 0.2 });
    obs.observe(el);
    return () => { obs.disconnect(); timers.current.forEach(clearTimeout); };
  }, [run]);

  return (
    <section style={{ background:'#080d1c', padding:'90px 5% 100px', position:'relative', overflow:'hidden' }}>
      {/* Subtle star-field */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none',
        backgroundImage:'radial-gradient(1px 1px at 20% 30%, rgba(255,255,255,.18) 0%, transparent 100%), radial-gradient(1px 1px at 60% 15%, rgba(255,255,255,.14) 0%, transparent 100%), radial-gradient(1px 1px at 80% 70%, rgba(255,255,255,.12) 0%, transparent 100%), radial-gradient(1px 1px at 40% 80%, rgba(255,255,255,.1) 0%, transparent 100%), radial-gradient(1px 1px at 10% 60%, rgba(255,255,255,.16) 0%, transparent 100%)',
        backgroundSize:'600px 400px' }} />
      {/* Moon glow */}
      <div style={{ position:'absolute', top:-120, right:'8%', width:300, height:300, borderRadius:'50%', background:'radial-gradient(circle, rgba(129,140,248,.08) 0%, transparent 70%)', pointerEvents:'none' }} />

      <div style={{ maxWidth:1080, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:72, alignItems:'center' }}>

        {/* Left — copy */}
        <div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:24, padding:'5px 14px', marginBottom:28, fontSize:12, color:'#475569', fontWeight:600 }}>
            <span style={{ fontSize:14 }}>🌙</span> Last night · 11:47 PM → 7:00 AM
          </div>
          <h2 style={{ fontSize:'clamp(28px,4vw,46px)', fontWeight:900, color:'#f8fafc', lineHeight:1.1, letterSpacing:'-1.5px', margin:'0 0 20px' }}>
            While you slept,<br/>
            <span style={{ background:'linear-gradient(135deg,#818cf8,#a78bfa)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Seva was working.</span>
          </h2>
          <p style={{ fontSize:16, color:'#475569', lineHeight:1.75, margin:'0 0 32px', maxWidth:380 }}>
            Every booking attempt — large parties, special requests, foreign languages — handled automatically. No phone calls. No voicemails. No missed revenue.
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              'Tables combined for large groups, no calls needed',
              'Special requests logged and assigned automatically',
              'Waitlist filled the moment a table freed up',
            ].map((pt, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, fontSize:14, color:'#94a3b8' }}>
                <span style={{ color:'#6366f1', flexShrink:0, fontWeight:700 }}>✓</span>{pt}
              </div>
            ))}
          </div>
        </div>

        {/* Right — animated feed */}
        <div ref={ref} style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.07)', borderRadius:22, overflow:'hidden', boxShadow:'0 40px 80px rgba(0,0,0,.5)' }}>
          {/* Feed header */}
          <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,.06)', display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,.02)' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#10b981', boxShadow:'0 0 0 3px rgba(16,185,129,.15)' }} />
            <span style={{ fontSize:12, fontWeight:700, color:'#475569' }}>Seva AI · Active overnight</span>
            <span style={{ marginLeft:'auto', fontSize:11, color:'#1e293b', fontWeight:600 }}>5 bookings handled</span>
          </div>

          {/* Events */}
          <div style={{ padding:'6px 0' }}>
            {NIGHT_EVENTS.map((ev, i) => (
              <div key={i} style={{
                display:'flex', alignItems:'flex-start', gap:13, padding:'12px 20px',
                opacity: count > i ? 1 : 0,
                transform: count > i ? 'none' : 'translateY(10px)',
                transition:'opacity .45s ease, transform .45s cubic-bezier(.22,1,.36,1)',
              }}>
                <div style={{ width:34, height:34, borderRadius:10, background:ev.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0, marginTop:1 }}>{ev.emoji}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#f1f5f9' }}>{ev.label}</span>
                    <span style={{ marginLeft:'auto', fontSize:10, color:'#334155', fontWeight:600, whiteSpace:'nowrap' }}>{ev.time}</span>
                  </div>
                  <span style={{ fontSize:11, color:'#475569', lineHeight:1.5 }}>{ev.detail}</span>
                </div>
              </div>
            ))}

            {/* Divider */}
            <div style={{ height:1, background:'rgba(255,255,255,.05)', margin:'4px 20px',
              opacity:showEnd?1:0, transition:'opacity .4s ease' }} />

            {/* Final — you wake up */}
            <div style={{
              display:'flex', alignItems:'flex-start', gap:13, padding:'14px 20px',
              opacity:showEnd?1:0, transform:showEnd?'none':'translateY(10px)',
              transition:'opacity .5s ease, transform .5s cubic-bezier(.22,1,.36,1)',
              background: showEnd ? 'rgba(52,211,153,.05)' : 'transparent',
            }}>
              <div style={{ width:34, height:34, borderRadius:10, background:NIGHT_FINAL.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0, marginTop:1 }}>{NIGHT_FINAL.emoji}</div>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:'#34d399' }}>{NIGHT_FINAL.label}</span>
                  <span style={{ marginLeft:'auto', fontSize:10, color:'#334155', fontWeight:600 }}>{NIGHT_FINAL.time}</span>
                </div>
                <span style={{ fontSize:11, color:'#6ee7b7', lineHeight:1.5 }}>{NIGHT_FINAL.detail}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ accordion item ─────────────────────────────────────────── */
function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`lp-rv lp-d${Math.min(i,7)}`}
      style={{ background:'rgba(255,255,255,.03)', border:`1px solid ${open?'rgba(99,102,241,.35)':'rgba(255,255,255,.07)'}`, borderRadius:14, overflow:'hidden', transition:'border-color .2s' }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'18px 22px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ flex:1, fontSize:15, fontWeight:700, color:'#f1f5f9', lineHeight:1.4 }}>{q}</span>
        <div style={{ width:24, height:24, borderRadius:'50%', background:open?'rgba(99,102,241,.2)':'rgba(255,255,255,.06)', border:`1px solid ${open?'rgba(99,102,241,.4)':'rgba(255,255,255,.1)'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all .2s' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={open?'#818cf8':'#475569'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transition:'transform .2s', transform:open?'rotate(180deg)':'none' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </button>
      <div style={{ maxHeight:open?300:0, overflow:'hidden', transition:'max-height .3s cubic-bezier(.4,0,.2,1)' }}>
        <p style={{ margin:0, padding:'0 22px 18px', fontSize:14, color:'#64748b', lineHeight:1.75 }}>{a}</p>
      </div>
    </div>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────── */
function Pill({ children, dark, green, light }) {
  const bg    = green ? '#f0fdf4' : dark ? 'rgba(79,70,229,.15)' : '#eef2ff';
  const color = green ? '#15803d' : dark ? '#a5b4fc'             : '#4f46e5';
  return <div style={{ display:'inline-block', background:bg, color, borderRadius:24, padding:'5px 15px', fontSize:12, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase', marginBottom:20 }}>{children}</div>;
}
function H2({ children, dark }) {
  return <h2 style={{ fontSize:'clamp(26px,4.5vw,46px)', fontWeight:900, letterSpacing:'-1px', lineHeight:1.15, color: dark ? '#0f172a' : '#f8fafc', margin:'0 0 16px' }}>{children}</h2>;
}
function Sub({ children, dark }) {
  return <p style={{ fontSize:18, color: dark ? '#64748b' : '#94a3b8', lineHeight:1.65, margin:'0 auto', maxWidth:520, textAlign:'center' }}>{children}</p>;
}
